import { Divider, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import Button from "components/basic/button";
import Radio from "components/basic/radio";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useNavigate } from "react-router-dom";
import SignUpCardLayout from "../../components/SignUpCardLayout";
import { useSnack } from "hooks/useSnack";
import { useFieldErrors } from "hooks/useFieldErrors";
import { Account } from "../..";
import { validateName } from "./validation";

interface NameFormProps {
  signUpData: Account;
  setSignUpData: React.Dispatch<React.SetStateAction<Account>>;
}

const NameForm: React.FC<NameFormProps> = ({ signUpData, setSignUpData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");

  const { errors, validateAll, revalidate } = useFieldErrors(validateName);

  // Applies an edit and re-checks any error already on screen.
  const handleChange = (name: string, value: string) => {
    const next = { ...signUpData, [name]: value };
    setSignUpData(next);
    revalidate(next);
  };

  const handleNextClick = () => {
    if (!validateAll(signUpData)) {
      showSnack(t("name.required"), "error");
      return;
    }

    navigate("?currentPage=addressForm");
  };

  return (
    <SignUpCardLayout>
      <div
        id="right section"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        }}
      >
        <Typography
          variant="h4"
          align="center"
          style={{ margin: `${theme.spacing(4)} 0` }}
        >
          {t("common.create_your_account")}
        </Typography>

        <TextField
          size="tiny"
          textAlign="center"
          name="firstName"
          autoComplete="given-name"
          value={signUpData.firstName}
          placeHolder={t("name.first_name")}
          onChange={({ target: { name, value } }) => handleChange(name, value)}
          error={errors.firstName ?? undefined}
        />

        <TextField
          size="tiny"
          textAlign="center"
          name="lastName"
          autoComplete="family-name"
          value={signUpData.lastName}
          placeHolder={t("name.last_name")}
          onChange={({ target: { name, value } }) => handleChange(name, value)}
          error={errors.lastName ?? undefined}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
          }}
        >
          <Radio
            isSelected={signUpData.userType === "user"}
            onChange={() =>
              setSignUpData((prev) => ({
                ...prev,
                userType: "user",
              }))
            }
            label={t("name.as_user")}
            value="user"
          />
          <Radio
            isSelected={signUpData.userType === "vendor"}
            onChange={() =>
              setSignUpData((prev) => ({
                ...prev,
                userType: "vendor",
              }))
            }
            label={t("name.as_vendor")}
            value="vendor"
          />
        </div>
      </div>

      <div>
        <div style={{ margin: `${theme.spacing(2)} 0` }}>
          <Button
            onClick={handleNextClick}
            variant="contained"
            color="warning"
            size="small"
            style={{ color: theme.palette.common.white }}
            fullWidth
          >
            {t("common.next")}
          </Button>
        </div>

        <Button
          onClick={() =>
            signUpData.authProvider === "google"
              ? navigate("?currentPage=signUpMethod")
              : navigate("?currentPage=emailForm")
          }
          variant="contained"
          size="small"
          style={{ color: theme.palette.common.white }}
          fullWidth
        >
          {t("common.back")}
        </Button>

        <Divider
          sx={{
            borderColor: theme.palette.common.white,
            margin: `${theme.spacing(3)} 0`,
          }}
        />

        <Button
          onClick={() => navigate("/signin")}
          variant="text"
          fullWidth
          size="small"
          style={{ color: theme.palette.common.white }}
        >
          <RichText
            t={t}
            i18nKey="common.have_account"
            components={{
              a: (
                <span
                  style={{ color: "orange", marginLeft: theme.spacing(1) }}
                />
              ),
            }}
          />
        </Button>
      </div>
    </SignUpCardLayout>
  );
};

export default NameForm;
